import { Color, Vec3 } from 'playcanvas';

import { Events } from './events';

type CaseType = 'accident' | 'premises' | 'crime-scene' | 'construction' | 'general';

type CaseMetadata = {
    caseType?: CaseType;
};

type AnnotationLayer = {
    id: string;
    name: string;
    visible: boolean;
    locked: boolean;
    color?: [number, number, number];
};

type ClipBoxState = {
    enabled?: boolean;
    center?: Vec3;
    halfExtents?: Vec3;
    rotation?: any;
};

const cloneClipBoxState = (value: unknown): ClipBoxState | undefined => {
    const clip = value as ClipBoxState | undefined;
    if (!clip) {
        return undefined;
    }

    return {
        enabled: clip.enabled,
        center: clip.center?.clone?.() ?? clip.center,
        halfExtents: clip.halfExtents?.clone?.() ?? clip.halfExtents,
        rotation: clip.rotation?.clone?.() ?? clip.rotation
    };
};

const ensureLayer = (events: Events, layer: AnnotationLayer) => {
    const layers = (events.invoke('annotations.layers') as AnnotationLayer[] | undefined) ?? [];
    if (layers.some(existing => existing.id === layer.id)) {
        return;
    }
    events.fire('annotations.layers.add', layer);
};

const registerScenePresets = (events: Events) => {
    let lastCaseType = ((events.invoke('case.metadata') as CaseMetadata | undefined)?.caseType ?? undefined);

    const baseline = {
        controlMode: (events.invoke('camera.controlMode') as 'orbit' | 'fly' | undefined) ?? 'orbit',
        gridVisible: !!events.invoke('grid.visible'),
        bgColor: ((events.invoke('bgClr') as Color | undefined)?.clone() ?? new Color(0, 0, 0, 1)),
        clipBox: cloneClipBoxState(events.invoke('clipBox'))
    };

    const applyGeneral = () => {
        events.fire('camera.setControlMode', baseline.controlMode);
        events.fire('grid.setVisible', baseline.gridVisible);
        events.fire('setBgClr', baseline.bgColor.clone());
        if (baseline.clipBox) {
            events.fire('clipBox.set', baseline.clipBox);
        } else {
            events.fire('clipBox.set', {
                enabled: false,
                center: { x: 0, y: 0, z: 0 } as Vec3,
                halfExtents: { x: 5, y: 5, z: 5 } as Vec3,
                rotation: { x: 0, y: 0, z: 0, w: 1 }
            });
        }
    };

    const applyPreset = (caseType: CaseType) => {
        switch (caseType) {
            case 'accident':
                events.fire('setBgClr', new Color(0.74, 0.81, 0.9, 1));
                events.fire('camera.setControlMode', 'fly');
                events.fire('grid.setVisible', true);
                events.fire('clipBox.set', {
                    enabled: false,
                    halfExtents: new Vec3(25, 10, 25)
                });
                break;
            case 'premises':
                events.fire('camera.setControlMode', 'fly');
                events.fire('grid.setVisible', false);
                break;
            case 'crime-scene':
                events.fire('camera.setControlMode', 'orbit');
                events.fire('setBgClr', new Color(0.12, 0.12, 0.12, 1));
                ensureLayer(events, {
                    id: 'preset-evidence-markers',
                    name: 'Evidence Markers',
                    visible: true,
                    locked: false
                });
                break;
            case 'construction':
                events.fire('camera.setControlMode', 'orbit');
                events.fire('clipBox.set', {
                    enabled: true
                });
                ensureLayer(events, {
                    id: 'preset-measurement-markers',
                    name: 'Measurement Layer',
                    visible: true,
                    locked: false
                });
                break;
            case 'general':
                applyGeneral();
                break;
        }
    };

    events.on('case.metadataChanged', (metadata: CaseMetadata | undefined) => {
        const nextCaseType = metadata?.caseType;
        if (!nextCaseType || nextCaseType === lastCaseType) {
            return;
        }
        lastCaseType = nextCaseType;
        applyPreset(nextCaseType);
    });
};

export { registerScenePresets };
